import { getAvatarTint } from "../../lib/getAvatarTint";
import { getInitials } from "../../lib/getInitials";
import { cn } from "../../lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

export interface PersonAvatarProps {
  /** Display name. Falls back to the email when a person hasn't set one. */
  name?: string | null;
  /** The identity the tint is derived from — stable across name edits. */
  email?: string | null;
  /** Their real picture, when the identity provider gave us one. */
  pictureUrl?: string | null;
  className?: string;
}

/**
 * One person, one avatar — everywhere.
 *
 * Their photo when there is one, otherwise a monogram on a tint derived from
 * their email. Deriving both from the same inputs is the whole point: this
 * used to be three separate implementations (a Gravatar call that always
 * resolved to the grey mystery-person silhouette, a hardcoded rust tile in
 * the rail, and a private `getInitials` that split names differently), so one
 * person could be a photo in one list, "?" on a rust disc in the rail, and a
 * gold monogram in another list. Route every avatar through here.
 */
export function PersonAvatar({
  name,
  email,
  pictureUrl,
  className,
}: PersonAvatarProps) {
  // The name is what reads as a monogram; the email is only a fallback so an
  // account with no name set still gets a letter instead of an empty disc.
  const label = name?.trim() || email?.trim() || "";

  return (
    <Avatar
      tint={getAvatarTint(email || label)}
      className={cn("size-8 shrink-0 text-xs", className)}
    >
      {pictureUrl ? <AvatarImage src={pictureUrl} alt={label} /> : null}
      <AvatarFallback>{getInitials(label)}</AvatarFallback>
    </Avatar>
  );
}
